import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useMsal } from '@azure/msal-react';
import { TanTable, BulkEditMeta } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import { TroubleTicketBadge } from '../../components/TroubleTicketBadge';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import { useSchedulerData } from '../../hooks/useSchedulerData';
import { useDateDragToFill } from '../../hooks/useDateDragToFill';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useToast } from '../../contexts/ToastContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { SchedulingLeg, ScheduleStatus, LCNextStep, LegScheduleUpdate, SCHEDULE_STATUS_LABELS, SCHEDULE_STATUS_COLORS } from '../../types/scheduling';
import LCStatusCountsWidget from './components/LCStatusCountsWidget';
import FinalizeModal from './components/FinalizeModal';
import LCBulkEditModal, { LCBulkEditUpdates } from './components/LCBulkEditModal';
import ReturnToSchedulerModal from './components/ReturnToSchedulerModal';
import { appendTimestampedComment } from '../../utils/schedulingComments';
import { countOrdersByRegion, buildRegionOptions } from '../../utils/regionOrderCounts';
import { timeOnLcSeverity, TIME_ON_LC_CLASSES, formatElapsed } from '../../utils/timeOnLc';
import { lastSelectedValue } from '../../utils/multiSelectSingle';
import { getSchedulingRowClassName } from '../../utils/schedulingRowClass';
import { SCHEDULING_MISSING_WEIGHT_TOOLTIP, legBlockedForMissingWeight } from '../../utils/importWeightRule';
import { SCHEDULING_MISSING_HAZ_DOCS_TOOLTIP, legBlockedForMissingHazDocs } from '../../utils/hazmatPaperworkRule';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import FilterBar from '../../components/FilterBar';
import api from '../../services/api';
import Notification from '../../components/Notification';
import { celebrateAchievement } from '../../utils/confetti';
import SchedulerExpandedPanel from './components/SchedulerExpandedPanel';
import SchedulingHistoryDrawer from './components/SchedulingHistoryDrawer';
import TimeInput, { parseTimeInput } from './components/TimeInput';
import {
  ClockIcon,
  CheckIcon,
  ArrowUturnLeftIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '../../components/ui/Tooltip';
import schedulingService from '../../services/scheduling.service';
import { SchedulingHistoryRecord } from '../../types/scheduling';
import { config } from '../../config/config';

// Helper to format dates, returning '-' for null, empty, or epoch dates (0 values in DB)
const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  // Append T00:00:00 to date-only strings to parse as local time, not UTC
  const parsed = new Date(date.includes('T') ? date : date + 'T00:00:00');
  if (isNaN(parsed.getTime())) return '-';
  // Epoch dates (year <= 1970) are invalid - DB stores 0 for empty dates
  if (parsed.getFullYear() <= 1970) return '-';
  return parsed.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

// Helper to check if a date is valid (not null, not invalid, not epoch)
const isValidDate = (date: string | null | undefined): boolean => {
  if (!date) return false;
  const parsed = new Date(date.includes('T') ? date : date + 'T00:00:00');
  if (isNaN(parsed.getTime())) return false;
  if (parsed.getFullYear() <= 1970) return false;
  return true;
};

// Helper to get LFD color class - Red only if today AND no prepull date
const getLfdColorClass = (lfdDate: string | null | undefined, prepullDate: string | null | undefined): string => {
  if (!isValidDate(lfdDate)) return '';
  // Once we have a prepull date, LFD is no longer an issue
  if (isValidDate(prepullDate)) return '';

  const parsed = new Date(lfdDate!.includes('T') ? lfdDate! : lfdDate! + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (parsed.getTime() === today.getTime()) return 'text-red-600';
  return '';
};

// Helper to get Cutoff/Per Diem color class - Red if today or past, orange if tomorrow
const getCutoffPerDiemColorClass = (date: string | null | undefined): string => {
  if (!isValidDate(date)) return '';

  const parsed = new Date(date!.includes('T') ? date! : date! + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (parsed.getTime() <= today.getTime()) return 'text-red-600';
  if (parsed.getTime() === tomorrow.getTime()) return 'text-orange-500';
  return '';
};

// Helper to format a Date to YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to get user initials
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Helper to get consistent user color
const getUserColor = (name: string): string => {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Border colors corresponding to bg colors (for field editing indicators)
const getUserBorderColor = (name: string): string => {
  const colors = [
    'border-blue-500', 'border-green-500', 'border-yellow-500', 'border-red-500',
    'border-purple-500', 'border-pink-500', 'border-indigo-500', 'border-teal-500',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Ring colors for field editing indicators
const getUserRingColor = (name: string): string => {
  const colors = [
    'ring-blue-500', 'ring-green-500', 'ring-yellow-500', 'ring-red-500',
    'ring-purple-500', 'ring-pink-500', 'ring-indigo-500', 'ring-teal-500',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Editable date picker with field editor indicator
interface EditableDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  editor: { user_name: string } | null;
  onFocus: () => void;
  onBlur: () => void;
  disabled?: boolean;
  // Drag-to-fill props
  rowIndex?: number;
  field?: string;
  dateStr?: string | null;
  onStartDrag?: (rowIndex: number, field: string, value: string) => void;
}

function EditableDatePicker({ value, onChange, editor, onFocus, onBlur, disabled, rowIndex, field, dateStr, onStartDrag }: EditableDatePickerProps) {
  const showHandle = !!dateStr && !disabled && onStartDrag !== undefined && rowIndex !== undefined && field !== undefined;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onFocus={onFocus}
      onBlur={onBlur}
      className={`relative group flex items-center gap-1 ${editor ? `ring-2 ${getUserRingColor(editor.user_name)} rounded` : ''}`}
    >
      <DateTimePicker
        mode="date"
        value={value}
        onChange={(val) => onChange(val instanceof Date ? val : null)}
        inputVariant="compact"
        autoApply
        usePortal
        autoFlip
        labelType="none"
        placeholder="Select date"
        disabled={disabled}
      />
      {editor && (
        <div
          className={`flex-shrink-0 h-5 w-5 rounded-full ${getUserColor(editor.user_name)} text-[0.5rem] text-white flex items-center justify-center font-medium border border-white shadow-sm`}
          title={`${editor.user_name} is editing`}
        >
          {getInitials(editor.user_name)}
        </div>
      )}
      {showHandle && (
        <div
          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-500 cursor-crosshair opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-sm"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onStartDrag!(rowIndex!, field!, dateStr!);
          }}
        />
      )}
    </div>
  );
}

// Editable time picker with field editor indicator

const STATUS_OPTIONS: MultiSelectOption[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending LC' },
  { value: 'HOLD', label: 'Hold' },
  { value: 'PENDING_CUSTOMER', label: 'Pending Customer' },
];

const MOVE_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'ALL', label: 'All Move Types' },
  { value: 'IMPORT', label: 'Import' },
  { value: 'EXPORT', label: 'Export' },
  { value: 'DOMESTIC', label: 'Domestic' },
];

const DELIVERY_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'LOCAL', label: 'Local' },
  { value: 'OTR', label: 'OTR' },
];

const PREPULL_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: 'all_prepulls', label: 'All Prepulls' },
  { value: 'not_finalized', label: 'Prepulls Not Finalized' },
  { value: 'finalized', label: 'Prepulls Finalized' },
  { value: 'no_prepull', label: 'No Prepull' },
];

const RESTRICTION_OPTIONS: MultiSelectOption[] = [
  {
    value: 'hot',
    label: 'HOT Load',
    icon: <span className="gw-icon gw-icon-hotjar text-brand-red" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'hazmat',
    label: 'Hazmat',
    icon: <span className="flaticon flaticon-warning text-orange-500" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'overweight',
    label: 'Overweight',
    icon: <span className="flaticon flaticon-weight text-heading" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'early',
    label: 'Early',
    icon: <span className="gw-icon gw-icon-early text-heading" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'triaxle',
    label: 'Triaxle',
    icon: <span className="gw-icon gw-icon-dual-axle text-blue-600" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'swing',
    label: 'Swing',
    icon: <span className="flaticon flaticon-cargo text-purple-600" style={{ fontSize: '18px' }}></span>
  },
  {
    value: 'tank',
    label: 'Tank',
    icon: <span className="flaticon flaticon-oil-tank text-purple-600" style={{ fontSize: '18px' }}></span>
  },
  {
    value: 'reefer',
    label: 'Reefer',
    icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="text-blue-500 fill-current"><path d="M11,24c-6.079.117-9.334-7.638-5-11.89V5c.211-6.609,9.791-6.6,10,0v7.11C20.335,16.363,17.077,24.117,11,24ZM11,2A3,3,0,0,0,8,5v7.537a1,1,0,0,1-.332.744A5.018,5.018,0,0,0,11,22a5.018,5.018,0,0,0,3.332-8.719A1,1,0,0,1,14,12.537V5A3,3,0,0,0,11,2Zm0,18a3.007,3.007,0,0,1-1-5.829V12a1,1,0,0,1,2,0v2.171A3.007,3.007,0,0,1,11,20Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,11,16ZM21,6a3,3,0,0,1,0-6A3,3,0,0,1,21,6Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,21,2Z"/></svg>
  }
];

function LogisticsCoordinatorContent() {
  const { accounts } = useMsal();
  const currentUser = accounts[0];
  const { showToast } = useToast();
  const { setDispatchButton } = useDispatchFooter();

  // Order details drawer state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // History data state (condensed strip in the row-expansion panel)
  const [historyData, setHistoryData] = useState<Record<number, SchedulingHistoryRecord[]>>({});
  const [historyLoading, setHistoryLoading] = useState<Set<number>>(new Set());

  // Full scheduling history drawer: pro set from a row link, null from the toolbar.
  const [historyDrawer, setHistoryDrawer] = useState<{ open: boolean; pronumber: number | null }>({
    open: false,
    pronumber: null,
  });
  const openHistoryDrawer = useCallback((pronumber: number | null) => {
    setHistoryDrawer({ open: true, pronumber });
  }, []);

  // Finalize modal state
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);

  // Bulk edit modal state
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [legsToFinalize, setLegsToFinalize] = useState<SchedulingLeg[]>([]);
  const [finalizing, setFinalizing] = useState(false);

  // Ref shared between drag-to-fill and scheduler data hooks to defer refetches during drag
  const dragIsDeferringRef = useRef(false);

  // Use scheduler data hook
  // Any orders-scope registry field can be added as a column. Rows are LEGS, so a
  // pro with several scheduled legs repeats the same order values down them.
  const fieldSelection = useAdditionalFieldSelection('logistics-coordinator');

  const {
    legs,
    filterOptions,
    selectedLegs,
    loading,
    loadingOptions,
    error,
    filters,
    setFilters,
    finalizeLegs,
    finalizePrepull,
    returnToScheduler,
    updateLegSchedule,
    toggleLegSelection,
    selectAllLegs,
    selectLegs,
    deselectLegs,
    clearSelection,
    refetch,
    activeUsers,
    isConnected,
    fieldEditors,
    broadcastFieldFocus,
    broadcastFieldBlur,
    broadcastFieldValue,
    getFieldEditor,
    newPronumbers,
    clearNewPronumbers,
  } = useSchedulerData({
    pageId: 'lc',
    userName: currentUser?.name,
    userId: currentUser?.localAccountId,
    userEmail: currentUser?.username,
    enableRealtime: true,
    initialFilters: {
      status: ['ALL'],
      include_null_status: false,
    },
    selectableStatuses: ['PENDING', 'HOLD', 'PENDING_CUSTOMER'],
    excludeStatusesOnAll: ['PLANNING', 'PENDING_APPT', 'RETURNED', 'RESCHEDULED', 'HOT_ORDER', 'FINALIZED'],
    isDeferringRefetch: dragIsDeferringRef,
    additionalFields: fieldSelection.additionalFields,
  });

  // Achievement state for "You've Got Pros" easter egg
  const [showAchievementNotification, setShowAchievementNotification] = useState(false);


  const handleNewProsEasterEgg = useCallback(() => {
    const userEmail = currentUser?.username || '';
    if (!userEmail) return;
    api.post('/achievements/record', { achievement_id: 'youve_got_pros', email: userEmail })
      .then(response => {
        if (response.data.first_discovery) {
          setShowAchievementNotification(true);
          celebrateAchievement();
        }
      })
      .catch(err => console.error('Failed to record achievement:', err));
  }, [currentUser?.username]);

  // Show toast when returning to page with new PROs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && newPronumbers.size > 0) {
        showToast({
          type: 'info',
          message: `You have ${newPronumbers.size} new Pro${newPronumbers.size === 1 ? '' : 's'} to Schedule`,
          duration: 0,
          onDismiss: clearNewPronumbers,
          onEasterEgg: handleNewProsEasterEgg,
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [newPronumbers, showToast, clearNewPronumbers, handleNewProsEasterEgg]);

  // Ref to hold current selection state - allows callbacks to read current value without stale closures
  const selectedLegsRef = useRef(selectedLegs);
  selectedLegsRef.current = selectedLegs;

  // Memoized selection size for column dependencies
  const selectedLegsSize = selectedLegs.size;

  // How many selected PROs already have a target date — Return to Scheduler
  // overwrites it, so the modal warns before it does
  const selectedWithTargetDate = useMemo(() => {
    if (selectedLegs.size === 0) return 0;
    return legs.filter(
      (leg) => selectedLegs.has(String(leg.pronumber)) && !leg.prepull_needed && !!leg.target_delivery_date
    ).length;
  }, [legs, selectedLegs]);

  // Terminal options from filter options, labeled with order counts from the
  // loaded grid data. Counts freeze while a region filter is active — the
  // fetch is region-filtered server-side, so other regions vanish from legs.
  const regionCountsRef = useRef<Map<string, number>>(new Map());
  const terminalOptions: MultiSelectOption[] = useMemo(() => {
    if (!filters.terminal?.length) {
      regionCountsRef.current = countOrdersByRegion(legs);
    }
    return buildRegionOptions(filterOptions?.terminals || [], regionCountsRef.current);
  }, [filterOptions, legs, filters.terminal]);

  // Customer options from filter options. When LCs are selected, cascade to only
  // customers belonging to those LCs (union across selected LCs); otherwise full list.
  const customerOptions: MultiSelectOption[] = useMemo(() => {
    const all = filterOptions?.customers || [];
    const selectedLCs = filters.logistic_coordinator;
    const visible = selectedLCs?.length
      ? all.filter((c) => c.lcs?.some((lc) => selectedLCs.includes(lc)))
      : all;
    return visible.map((c) => ({ value: c.value, label: c.label }));
  }, [filterOptions, filters.logistic_coordinator]);

  // Logistic Coordinator options from filter options (full list so labels persist across filter changes)
  const lcOptions: MultiSelectOption[] = useMemo(() => {
    return filterOptions?.logistics_coordinators.map((lc) => ({ value: lc.value, label: lc.label })) || [];
  }, [filterOptions]);

  // Auto-select the logged-in user in the LC filter on initial load
  const hasAutoSelectedLC = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedLC.current || !lcOptions.length || !currentUser?.name) return;
    const match = lcOptions.find(
      (opt) => opt.label.toLowerCase() === currentUser.name!.toLowerCase()
    );
    if (match) {
      hasAutoSelectedLC.current = true;
      setFilters({ ...filters, logistic_coordinator: [match.value] });
    }
  }, [lcOptions, currentUser?.name]);

  // Pickup location options from filter options
  const pickupLocationOptions: MultiSelectOption[] = useMemo(() => {
    return filterOptions?.pickup_locations.map((loc) => ({ value: loc, label: loc })) || [];
  }, [filterOptions]);

  // Delivery location options from filter options
  const deliveryLocationOptions: MultiSelectOption[] = useMemo(() => {
    return filterOptions?.delivery_locations.map((loc) => ({ value: loc, label: loc })) || [];
  }, [filterOptions]);

  // Track filtered rows from TanTable (after column/global filters applied)
  const [filteredGroupedLegs, setFilteredGroupedLegs] = useState<SchedulingLeg[]>([]);

  // Group legs by pronumber for display (one row per order, expandable to show all legs)
  const groupedLegs = useMemo(() => {
    const groups = new Map<number, SchedulingLeg[]>();

    // Group legs by pronumber
    legs.forEach((leg) => {
      const existing = groups.get(leg.pronumber) || [];
      existing.push(leg);
      groups.set(leg.pronumber, existing);
    });

    // Convert to order-level rows with all legs stored for expanded content
    const result: SchedulingLeg[] = [];
    groups.forEach((groupLegs) => {
      // Sort legs by leg_num
      groupLegs.sort((a, b) => a.leg_num - b.leg_num);

      const firstLeg = groupLegs[0];
      const lastLeg = groupLegs[groupLegs.length - 1];

      // Create order-level row with all legs attached
      result.push({
        ...firstLeg,
        pickup_name: firstLeg.orig_name,
        delivery_name: lastLeg.dest_name,
        allLegs: groupLegs, // Store all legs for expanded content
      });
    });

    return result;
  }, [legs]);

  // Drag-to-fill for Target Delivery Date
  const getRowLocked = useCallback((rowIndex: number, _field: string) => {
    const row = groupedLegs[rowIndex];
    if (!row) return true;
    return row.schedule_status === 'FINALIZED';
  }, [groupedLegs]);

  const handleDragFillUpdate = useCallback((rowIndex: number, field: string, value: string) => {
    const row = groupedLegs[rowIndex];
    if (!row) return;
    selectLegs([String(row.pronumber)]);
    // Return the promise so drag-to-fill can await all API calls before refetching
    if (field === 'lc_next_step') {
      // Status drag needs to update both lc_next_step and schedule_status
      let newStatus: ScheduleStatus | undefined;
      if (value === 'PENDING_LC') newStatus = 'PENDING';
      else if (value === 'PENDING_CUSTOMER') newStatus = 'PENDING_CUSTOMER';
      else if (value === 'HOLD') newStatus = 'HOLD';
      return updateLegSchedule(row.pronumber, {
        lc_next_step: value as LCNextStep,
        ...(newStatus && { schedule_status: newStatus }),
        // HOLD parks the order without a date; return to scheduler to get a new one
        ...(newStatus === 'HOLD' && { proposed_appt_date: null }),
      });
    } else {
      return updateLegSchedule(row.pronumber, { [field]: value });
    }
  }, [groupedLegs, updateLegSchedule, selectLegs]);

  // Handle drag end: unset deferral flag and flush any pending refetch
  const handleDragEnd = useCallback(() => {
    dragIsDeferringRef.current = false;
    refetch();
  }, [refetch]);

  const { startDrag: startDateDragRaw } = useDateDragToFill({
    getRowLocked,
    updateRow: handleDragFillUpdate,
    onDragEnd: handleDragEnd,
  });

  // Wrap startDrag to set the deferral flag before the drag begins
  const startDateDrag = useCallback((rowIndex: number, field: string, value: string) => {
    dragIsDeferringRef.current = true;
    startDateDragRaw(rowIndex, field, value);
  }, [startDateDragRaw]);

  // Count active advanced filters
  const activeAdvancedFilterCount = useMemo(() => {
    let count = 0;
    if (filters.pickup_from) count++;
    if (filters.deliver_to) count++;
    if (filters.restriction) count++;
    if (filters.appt_date) count++;
    return count;
  }, [filters.pickup_from, filters.deliver_to, filters.restriction, filters.appt_date]);

  // Dynamic table title based on filters
  const tableTitle = useMemo(() => {
    const parts: string[] = [];

    // Move type (always present)
    if (filters.move_type === 'ALL') {
      parts.push('All Move Types');
    } else {
      parts.push(filters.move_type.charAt(0) + filters.move_type.slice(1).toLowerCase());
    }

    // Delivery type
    if (filters.delivery_type) {
      parts.push(filters.delivery_type);
    }

    // Terminal
    if (filters.terminal && filters.terminal.length > 0) {
      parts.push(filters.terminal.join(', '));
    }

    // Customers (show count if multiple, resolve code to name)
    if (filters.customer && filters.customer.length > 0) {
      if (filters.customer.length === 1) {
        const match = customerOptions.find(o => o.value === filters.customer![0]);
        parts.push(match ? match.label : filters.customer[0]);
      } else {
        parts.push(`${filters.customer.length} Customers`);
      }
    }

    // Advanced filters
    if (activeAdvancedFilterCount > 0) {
      const advParts: string[] = [];
      if (filters.pickup_from) advParts.push(filters.pickup_from);
      if (filters.deliver_to) advParts.push(filters.deliver_to);
      if (filters.restriction) advParts.push(filters.restriction);
      parts.push(`(${advParts.join(', ')})`);
    }

    return `Scheduling: ${parts.join(' - ')}`;
  }, [filters.move_type, filters.delivery_type, filters.terminal, filters.customer, filters.pickup_from, filters.deliver_to, filters.restriction, activeAdvancedFilterCount, customerOptions]);

  // Auto-select a row when a date is entered
  const handleAutoSelect = useCallback((pronumber: number) => {
    selectLegs([String(pronumber)]);
  }, [selectLegs]);

  // Right-click bulk edit: TanTable lets the user right-click + drag down an
  // opted-in column (meta.bulkEdit), then apply one value to all selected rows.
  // Lock rules mirror the inline cell editors; locked rows are skipped.
  const handleColumnBulkEdit = useCallback(async (columnId: string, rows: SchedulingLeg[], value: unknown) => {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!raw) {
      showToast({ type: 'warning', message: 'No value entered — nothing applied. Clear fields individually instead.' });
      return;
    }

    let update: LegScheduleUpdate;
    let isRowLocked: (row: SchedulingLeg) => boolean;

    if (columnId === 'target_delivery_date') {
      update = { target_delivery_date: raw };
      isRowLocked = (row) => row.schedule_status === 'FINALIZED';
    } else if (columnId === 'appt_time_start' || columnId === 'appt_time_end') {
      const parsed = parseTimeInput(raw);
      if (!parsed) {
        showToast({ type: 'error', message: `Could not parse "${raw}" as a time — try "3pm", "1530", or "3:30 pm"` });
        return;
      }
      update = { [columnId]: parsed };
      isRowLocked = () => false;
    } else if (columnId === 'schedule_status') {
      const nextStep = raw as LCNextStep;
      let newStatus: ScheduleStatus | null = null;
      if (nextStep === 'PENDING_LC') newStatus = 'PENDING';
      else if (nextStep === 'PENDING_CUSTOMER') newStatus = 'PENDING_CUSTOMER';
      else if (nextStep === 'HOLD') newStatus = 'HOLD';
      update = {
        lc_next_step: nextStep,
        ...(newStatus && { schedule_status: newStatus }),
        // HOLD parks the order without a date; return to scheduler to get a new one
        ...(newStatus === 'HOLD' && { proposed_appt_date: null }),
      };
      // Status is only editable while the row is still in an LC-editable state
      isRowLocked = (row) => !['PENDING', 'PENDING_CUSTOMER', 'HOLD'].includes(row.schedule_status || '');
    } else {
      return;
    }

    let updated = 0;
    let skipped = 0;
    let failed = 0;
    for (const row of rows) {
      if (isRowLocked(row)) {
        skipped++;
        continue;
      }
      try {
        await updateLegSchedule(row.pronumber, update);
        // Dates auto-select the row, matching the inline editors
        if (columnId === 'target_delivery_date') {
          handleAutoSelect(row.pronumber);
        }
        updated++;
      } catch {
        failed++; // updateLegSchedule already refetches on failure
      }
    }

    const parts = [`Updated ${updated} row${updated === 1 ? '' : 's'}`];
    if (skipped) parts.push(`${skipped} locked (skipped)`);
    if (failed) parts.push(`${failed} failed`);
    showToast({ type: failed ? 'error' : 'success', message: parts.join(', ') });
  }, [updateLegSchedule, handleAutoSelect, showToast]);

  // Handle finalize - re-fetches fresh leg data from CMS then opens modal
  const handleFinalize = useCallback(async () => {
    const selectedKeys = Array.from(selectedLegsRef.current);

    if (selectedKeys.length === 0) {
      showToast({
        type: 'warning',
        message: 'No legs selected to finalize',
      });
      return;
    }

    // Get the local leg objects as a starting point
    const selectedLegObjects = legs.filter((leg) => {
      const key = String(leg.pronumber);
      return selectedLegsRef.current.has(key);
    });

    // Re-fetch fresh data for each selected PRO to get current has_prepull_leg from CMS
    try {
      const freshResults = await Promise.all(
        selectedLegObjects.map((leg) => schedulingService.getOrderLegs(leg.pronumber))
      );
      // Merge fresh has_prepull_leg / proposed dates into selected legs
      const freshByPro = new Map<number, SchedulingLeg>();
      freshResults.flat().forEach((leg) => freshByPro.set(leg.pronumber, leg));

      const mergedLegs = selectedLegObjects.map((leg) => {
        const fresh = freshByPro.get(leg.pronumber);
        if (fresh) {
          // is_missing_weight / is_missing_haz_docs ride along so a weight or a
          // haz doc entered a moment ago lifts the block without a grid refresh.
          return { ...leg, has_prepull_leg: fresh.has_prepull_leg, proposed_prepull_date: fresh.proposed_prepull_date, proposed_appt_date: leg.confirmed_delivery_date || fresh.proposed_appt_date, is_missing_weight: fresh.is_missing_weight, is_missing_haz_docs: fresh.is_missing_haz_docs, is_hazmat: fresh.is_hazmat ?? leg.is_hazmat, move_type: fresh.move_type ?? leg.move_type };
        }
        return leg;
      });

      setLegsToFinalize(mergedLegs);
    } catch {
      // If fresh fetch fails, fall back to local data
      setLegsToFinalize(selectedLegObjects);
    }

    setIsFinalizeModalOpen(true);
  }, [legs, showToast]);

  // Confirm finalize - called when modal confirms
  const confirmFinalize = useCallback(async () => {
    const prosData = legsToFinalize.map((leg) => ({
      pronumber: leg.pronumber,
    }));

    setFinalizing(true);
    try {
      const { count, errors } = await finalizeLegs(prosData);
      if (errors && errors.length > 0) {
        showToast({
          type: count > 0 ? 'warning' : 'error',
          message: count > 0
            ? `Finalized ${count} leg${count !== 1 ? 's' : ''}, but ${errors.length} failed: ${errors.join('; ')}`
            : `Failed to finalize: ${errors.join('; ')}`,
        });
      } else {
        showToast({
          type: 'success',
          message: `Finalized ${count} leg${count !== 1 ? 's' : ''}`,
        });
      }
      setIsFinalizeModalOpen(false);
      setLegsToFinalize([]);
    } catch (err: any) {
      showToast({
        type: 'error',
        message: err.message || 'Failed to finalize legs',
      });
    } finally {
      setFinalizing(false);
    }
  }, [legsToFinalize, finalizeLegs, showToast]);

  // Handle finalize prepull only
  const handleFinalizePrepull = useCallback(async () => {
    const selectedKeys = Array.from(selectedLegsRef.current);
    if (selectedKeys.length === 0) {
      showToast({ type: 'warning', message: 'No legs selected' });
      return;
    }

    const pendingApproval = legs.filter(
      leg => selectedLegsRef.current.has(String(leg.pronumber)) && leg.overweight_approval_pending
    );
    if (pendingApproval.length > 0) {
      showToast({
        type: 'error',
        message: `Cannot finalize prepull: Overweight permit approval required for PRO ${pendingApproval.map(leg => leg.pronumber).join(', ')}.`,
      });
      return;
    }

    // IMPORT PROs with no weight can't be scheduled (backend rejects too) — say
    // so up front instead of round-tripping a partial-failure toast.
    const blocked = legs.filter(
      (leg) => selectedLegsRef.current.has(String(leg.pronumber)) && legBlockedForMissingWeight(leg)
    );
    if (blocked.length > 0) {
      showToast({
        type: 'error',
        message: `Cannot finalize prepull: weight is required on IMPORT orders. Missing on PRO ${blocked.map((l) => l.pronumber).join(', ')}.`,
      });
      return;
    }

    // Haz=Y PROs with no HAZ-MAT PAPERWORK can't be scheduled (backend rejects too).
    const blockedHaz = legs.filter(
      (leg) => selectedLegsRef.current.has(String(leg.pronumber)) && legBlockedForMissingHazDocs(leg)
    );
    if (blockedHaz.length > 0) {
      showToast({
        type: 'error',
        message: `Cannot finalize prepull: hazmat paperwork is required on Haz=Y orders. Upload the HAZ-MAT PAPERWORK document on PRO ${blockedHaz.map((l) => l.pronumber).join(', ')}.`,
      });
      return;
    }

    const prosData = selectedKeys.map((key) => ({ pronumber: Number(key) }));
    try {
      const { count, errors } = await finalizePrepull(prosData);
      if (errors && errors.length > 0) {
        showToast({
          type: count > 0 ? 'warning' : 'error',
          message: count > 0
            ? `Finalized prepull for ${count} PRO${count !== 1 ? 's' : ''}, but ${errors.length} failed: ${errors.join('; ')}`
            : `Failed to finalize prepull: ${errors.join('; ')}`,
        });
      } else {
        showToast({
          type: 'success',
          message: `Finalized prepull for ${count} PRO${count !== 1 ? 's' : ''}`,
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        message: err.message || 'Failed to finalize prepull',
      });
    }
  }, [legs, finalizePrepull, showToast]);

  // Handle return to scheduler — prompt for an optional comment first
  const handleReturn = useCallback(() => {
    if (selectedLegsRef.current.size === 0) {
      showToast({ type: 'warning', message: 'No PROs selected' });
      return;
    }
    setIsReturnModalOpen(true);
  }, [showToast]);

  const confirmReturn = useCallback(async (targetDate: string, comment: string, targetOnOrBy: string) => {
    const pronumbers = Array.from(selectedLegsRef.current).map(Number);

    // Append the comment (timestamped) to every selected PRO before returning
    if (comment) {
      for (const pronumber of pronumbers) {
        const leg = legs.find((l) => l.pronumber === pronumber);
        try {
          await updateLegSchedule(pronumber, {
            scheduling_comments: appendTimestampedComment(leg?.scheduling_comments, comment),
          });
        } catch {
          // Individual errors surfaced by updateLegSchedule; still attempt the return
        }
      }
    }

    try {
      const { count, errors } = await returnToScheduler(
        pronumbers.map((pronumber) => ({ pronumber })),
        targetDate || undefined,
        targetOnOrBy === 'ON' || targetOnOrBy === 'BY' ? targetOnOrBy : undefined
      );
      if (errors && errors.length > 0) {
        // PROs whose target date failed to write are still on the LC
        showToast({
          type: count > 0 ? 'warning' : 'error',
          message: count > 0
            ? `Returned ${count} leg${count !== 1 ? 's' : ''}, but ${errors.length} failed: ${errors.join('; ')}`
            : `Failed to return: ${errors.join('; ')}`,
        });
      } else {
        showToast({
          type: 'success',
          message: `Returned ${count} leg${count !== 1 ? 's' : ''} to scheduler`,
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        message: err.message || 'Failed to return legs',
      });
    }
  }, [legs, updateLegSchedule, returnToScheduler, showToast]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedLegsRef.current.size === 0) {
      showToast({ type: 'warning', message: 'No PROs selected' });
      return;
    }
    setIsBulkEditModalOpen(true);
  }, [showToast]);

  const confirmBulkEdit = useCallback(async (updates: LCBulkEditUpdates) => {
    const selectedKeys = Array.from(selectedLegsRef.current);
    let successCount = 0;

    for (const key of selectedKeys) {
      const pronumber = Number(key);
      const leg = legs.find((l) => l.pronumber === pronumber);
      if (!leg) continue;

      const legUpdate: Record<string, any> = {};

      if (updates.schedule_status) {
        legUpdate.schedule_status = updates.schedule_status;
      }

      if (updates.scheduling_comment) {
        legUpdate.scheduling_comments = appendTimestampedComment(
          leg.scheduling_comments,
          updates.scheduling_comment
        );
      }

      if (Object.keys(legUpdate).length === 0) continue;

      try {
        await updateLegSchedule(pronumber, legUpdate);
        successCount++;
      } catch {
        // Individual errors handled by updateLegSchedule
      }
    }

    if (successCount > 0) {
      showToast({
        type: 'success',
        message: `Updated ${successCount} PRO${successCount !== 1 ? 's' : ''}`,
      });
    }
  }, [legs, updateLegSchedule, showToast]);

  // Set footer buttons with active users display
  useEffect(() => {
    // Filter active users to exclude current user and duplicates
    // Identify ourselves by account id, not by socket id: presence is keyed per
    // user server-side, so with the page open in two tabs only the newest tab's
    // sid appears in the list and the older one would fail to find itself.
    const currentUserId = currentUser?.localAccountId;
    const seen = new Set<string>();
    const otherUsers = activeUsers.filter(user => {
      if (user.user_id === currentUserId || seen.has(user.user_id)) {
        return false;
      }
      seen.add(user.user_id);
      return true;
    });
    const maxVisible = 10;
    const visibleUsers = otherUsers.slice(0, maxVisible);
    const overflowUsers = otherUsers.slice(maxVisible);

    const getInitials = (name: string): string => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const getUserColor = (name: string): string => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      ];
      const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    setDispatchButton(
      <div className="flex items-center justify-between w-full">
        {/* Left side - Active users */}
        <div className="flex items-center gap-2">
          {otherUsers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <UsersIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <div className="flex -space-x-1">
                {visibleUsers.map(user => (
                  <Tooltip key={user.user_id} content={user.user_name}>
                    <div
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 text-[0.625rem] font-medium text-white cursor-pointer ${getUserColor(user.user_name)}`}
                    >
                      {getInitials(user.user_name)}
                    </div>
                  </Tooltip>
                ))}
                {overflowUsers.length > 0 && (
                  <Tooltip
                    content={
                      <div className="flex flex-col gap-1">
                        {overflowUsers.map(user => (
                          <span key={user.user_id}>{user.user_name}</span>
                        ))}
                      </div>
                    }
                  >
                    <div className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 bg-gray-500 text-[0.625rem] font-medium text-white cursor-pointer">
                      +{overflowUsers.length}
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Right side - Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleBulkEdit}
            disabled={selectedLegs.size === 0}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <PencilSquareIcon className="w-4 h-4" />
            Bulk Edit ({selectedLegs.size})
          </button>
          <button
            onClick={handleReturn}
            disabled={selectedLegs.size === 0}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
            Return ({selectedLegs.size})
          </button>
          <button
            onClick={handleFinalizePrepull}
            disabled={selectedLegs.size === 0}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckIcon className="w-4 h-4" />
            Finalize Prepull ({selectedLegs.size})
          </button>
          <button
            onClick={handleFinalize}
            disabled={selectedLegs.size === 0}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckIcon className="w-4 h-4" />
            Finalize ({selectedLegs.size})
          </button>
        </div>
      </div>
    );

    return () => setDispatchButton(null);
  }, [selectedLegs.size, setDispatchButton, handleFinalize, handleFinalizePrepull, handleReturn, handleBulkEdit, activeUsers, currentUser?.localAccountId]);

  // Open order details drawer
  const openOrderModal = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsModalOpen(true);
  }, []);

  // Close order details drawer
  const closeOrderModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPro(null);
  }, []);

  // Equipment icons renderer
  const renderEquipmentIcons = useCallback((leg: SchedulingLeg) => {
    const icons = [];
    if (leg.is_hazmat) {
      icons.push(
        <span key="hazmat" className="flaticon flaticon-warning text-orange-500" style={{ fontSize: '16px' }} title="Hazmat" />
      );
    }
    if (leg.is_reefer) {
      icons.push(
        <span key="reefer" title="Reefer">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="text-blue-500 fill-current">
            <path d="M11,24c-6.079.117-9.334-7.638-5-11.89V5c.211-6.609,9.791-6.6,10,0v7.11C20.335,16.363,17.077,24.117,11,24ZM11,2A3,3,0,0,0,8,5v7.537a1,1,0,0,1-.332.744A5.018,5.018,0,0,0,11,22a5.018,5.018,0,0,0,3.332-8.719A1,1,0,0,1,14,12.537V5A3,3,0,0,0,11,2Zm0,18a3.007,3.007,0,0,1-1-5.829V12a1,1,0,0,1,2,0v2.171A3.007,3.007,0,0,1,11,20Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,11,16ZM21,6a3,3,0,0,1,0-6A3,3,0,0,1,21,6Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,21,2Z"/>
          </svg>
        </span>
      );
    }
    if (leg.is_93k) {
      icons.push(
        <span key="93k" className="gw-icon text-heading" style={{ fontSize: '16px' }} title="93K">&#xE9E5;</span>
      );
    }
    if (leg.is_tank) {
      icons.push(
        <span key="tank" className="flaticon flaticon-oil-tank text-purple-600" style={{ fontSize: '16px' }} title="Tank" />
      );
    }
    if (leg.is_hot) {
      icons.push(
        <span key="hot" className="gw-icon gw-icon-hotjar text-red-500" style={{ fontSize: '16px' }} title="Hot" />
      );
    }
    if (leg.is_overweight || leg.overweight_approval_pending) {
      icons.push(
        <span key="overweight" className={`flaticon flaticon-weight ${leg.overweight_approval_pending ? 'text-red-600' : 'text-heading'}`} style={{ fontSize: '16px' }} title={leg.overweight_approval_pending ? "Overweight permit approval required; LC cannot finalize" : "Overweight"} />
      );
    }
    if (leg.is_daycab) {
      icons.push(
        <span key="daycab" className="gw-icon gw-icon-daycab text-blue-700" style={{ fontSize: '16px' }} title="Daycab" />
      );
    }
    if (leg.is_triaxle) {
      icons.push(
        <span key="triaxle" className="gw-icon gw-icon-dual-axle text-blue-600" style={{ fontSize: '16px' }} title="Triaxle" />
      );
    }
    if (leg.is_swing) {
      icons.push(
        <span key="swing" className="flaticon flaticon-cargo text-purple-600" style={{ fontSize: '16px' }} title="Swing" />
      );
    }
    if (leg.is_ppe_required) {
      icons.push(
        <ShieldCheckIcon key="ppe" className="h-4 w-4 text-yellow-600 dark:text-yellow-400" aria-label="PPE Required" />
      );
    }
    return <div className="flex gap-1">{icons}</div>;
  }, []);

  // Flag icons renderer (Reschedule Required, Missing Weight, etc.)
  const renderFlagIcons = useCallback((leg: SchedulingLeg) => {
    const flags = [];
    if (leg.is_reschedule_required) {
      const rescheduleHistory = leg.reschedule_history || [];
      const tooltipContent = rescheduleHistory.length > 0 ? (
        <div className="text-xs space-y-2 max-w-xs">
          <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
            Service Failure History ({leg.reschedule_count})
          </div>
          {rescheduleHistory.map((entry, idx) => (
            <div key={idx} className="space-y-0.5">
              <div className="text-gray-500">{entry.old_date} → {entry.new_date}</div>
              <div className="text-gray-900 font-medium">{entry.reason || 'No reason provided'}</div>
              <div className="text-gray-400 text-[0.625rem]">{entry.created}</div>
            </div>
          ))}
        </div>
      ) : `${leg.reschedule_count} service failure${leg.reschedule_count !== 1 ? 's' : ''}`;

      flags.push(
        <Tooltip key="reschedule" content={tooltipContent} placement="right">
          <span
            className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${
              leg.reschedule_count >= 2 ? 'bg-red-500' : 'bg-orange-500'
            }`}
          >
            {leg.reschedule_count}
          </span>
        </Tooltip>
      );
    }
    // Backend sets is_missing_weight only for IMPORT PROs with lbs NULL/0.
    // Text pill (like COD) rather than an icon: Finalize is blocked on it.
    if (leg.is_missing_weight) {
      flags.push(
        <Tooltip key="missing-weight" content={SCHEDULING_MISSING_WEIGHT_TOOLTIP} placement="top">
          <span className="inline-flex items-center justify-center px-1 h-5 text-[0.625rem] font-bold text-white rounded bg-red-600 cursor-help">
            NO WT
          </span>
        </Tooltip>
      );
    }
    // Backend sets is_missing_haz_docs only for Haz=Y PROs with no HAZ-MAT PAPERWORK doc.
    if (legBlockedForMissingHazDocs(leg)) {
      flags.push(
        <Tooltip key="missing-haz-docs" content={SCHEDULING_MISSING_HAZ_DOCS_TOOLTIP} placement="top">
          <span className="inline-flex items-center justify-center px-1 h-5 text-[0.625rem] font-bold text-white rounded bg-red-600 cursor-help">
            NO HAZ DOCS
          </span>
        </Tooltip>
      );
    }
    if (leg.is_cod) {
      flags.push(
        <Tooltip key="cod" content="COD — Cash on Delivery" placement="top">
          <span className="inline-flex items-center justify-center px-1 h-5 text-[0.625rem] font-bold text-white rounded bg-green-600 cursor-help">
            COD
          </span>
        </Tooltip>
      );
    }
    if (leg.inbond_type?.trim()) {
      flags.push(
        <Tooltip key="bonded" content={`Bonded Cargo: ${leg.inbond_type}`} placement="top">
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded bg-blue-600 cursor-help">
            B
          </span>
        </Tooltip>
      );
    }
    // Check if rail order is missing rail pickup number
    const isRailOrder = ((leg.mode === 'MLB' && leg.move_type === 'IMPORT') || leg.move_type === 'RAIL TO PORT') && (leg.orig_location_type === 'RAIL' || leg.orig_require_pickup === 'Y');
    if (isRailOrder && !leg.rail_pickup_number?.trim()) {
      flags.push(
        <Tooltip key="missing-rail-pickup" content="Missing Rail Pickup #" placement="top">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-red-500 fill-current cursor-help">
            <path d="m24,21l-.294-3h-2.336l-.196-2h2.336l-.294-3h-2.336l-.196-2h2.336l-.294-3h-2.336l-.196-2h2.336l-.294-3h-2.336l-.294-3h-3.014l.294,3H7.115l.294-3h-3.014l-.294,3H1.765l-.294,3h2.336l-.196,2H1.274l-.294,3h2.336l-.196,2H.784l-.294,3h2.336l-.196,2H.294l-.294,3h2.336l-.294,3h3.014l.294-3h13.3l.294,3h3.014l-.294-3h2.336ZM6.821,6h10.359l.196,2H6.625l.196-2Zm-.49,5h11.339l.196,2H6.134l.196-2Zm-.686,7l.196-2h12.319l.196,2H5.644Z"/>
          </svg>
        </Tooltip>
      );
    }
    // Show comment icon if scheduling comments exist
    if (leg.scheduling_comments?.trim()) {
      flags.push(
        <Tooltip key="comment" content={leg.scheduling_comments} placement="top">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 text-blue-500 cursor-help">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
          </svg>
        </Tooltip>
      );
    }
    return flags.length > 0 ? <div className="flex gap-1 items-center">{flags}</div> : null;
  }, []);

  const renderExpandedContent = useCallback((row: SchedulingLeg) => {
    // Fetch history when expanded
    if (!historyData[row.pronumber] && !historyLoading.has(row.pronumber)) {
      setHistoryLoading(prev => new Set(prev).add(row.pronumber));
      schedulingService.getHistory(row.pronumber).then(data => {
        setHistoryData(prev => ({ ...prev, [row.pronumber]: data }));
      }).catch(() => {
        showToast({ type: 'error', message: 'Failed to load history' });
      }).finally(() => {
        setHistoryLoading(prev => {
          const next = new Set(prev);
          next.delete(row.pronumber);
          return next;
        });
      });
    }

    return (
      <SchedulerExpandedPanel
        row={row}
        historyRecords={historyData[row.pronumber] || []}
        historyLoading={historyLoading.has(row.pronumber)}
        onUpdateSchedule={updateLegSchedule}
        showStatus
      />
    );
  }, [updateLegSchedule, historyData, historyLoading, showToast]);

  // Table columns
  const baseColumns: ColumnDef<SchedulingLeg>[] = useMemo(
    () => [
      {
        accessorKey: 'pronumber',
        header: 'Pro #',
        size: 100,
        cell: ({ row }) => {
          const pronumber = row.original.pronumber;
          // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
          // trackViewedColumnId="pronumber" wrapper.
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openOrderModal(pronumber);
                }}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                {pronumber}
              </button>
              <a
                href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="Open in CMS V1"
                aria-label="Open in CMS V1"
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openHistoryDrawer(pronumber);
                }}
                className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Scheduling History"
                aria-label={`Scheduling history for pro ${pronumber}`}
              >
                <ClockIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        },
      },
      {
        id: 'select',
        header: ({ table }) => {
          const pageRows = table.getRowModel().rows;
          const selectableRows = pageRows.filter(r => r.original.schedule_status !== 'FINALIZED');
          const allSelected = selectableRows.length > 0 && selectableRows.every(r => selectedLegsRef.current.has(String(r.original.pronumber)));
          return (
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => {
                const keys = selectableRows.map(r => String(r.original.pronumber));
                if (allSelected) {
                  deselectLegs(keys);
                } else {
                  selectLegs(keys);
                }
              }}
              className="h-4 w-4 rounded border-gray-300"
            />
          );
        },
        cell: ({ row }) => {
          const currentSelection = selectedLegsRef.current;
          const isLocked = row.original.schedule_status === 'FINALIZED';
          const key = String(row.original.pronumber);
          return (
            <input
              type="checkbox"
              checked={currentSelection.has(key)}
              onChange={(e) => {
                e.stopPropagation();
                toggleLegSelection(row.original.pronumber);
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300"
              disabled={isLocked}
            />
          );
        },
        size: 40,
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        size: 120,
      },
      {
        accessorKey: 'po',
        header: 'PO #',
        size: 120,
      },
      {
        accessorKey: 'container_number',
        header: 'Container #',
        size: 120,
      },
      {
        accessorKey: 'bill_to_ref',
        header: 'Bill To Ref #',
        size: 120,
      },
      {
        accessorKey: 'bol',
        header: 'Booking #',
        size: 120,
      },
      {
        accessorKey: 'logistics_coordinator',
        header: 'LC',
        size: 100,
      },
      {
        accessorKey: 'move_type',
        header: 'Move Type',
        size: 110,
      },
      {
        id: 'time_on_lc',
        header: 'Time on LC',
        size: 90,
        accessorFn: (row: SchedulingLeg) => row.sent_to_lc_at,
        cell: ({ row }) => {
          const ts = row.original.sent_to_lc_at;
          const display = formatElapsed(ts);
          const severityClass = TIME_ON_LC_CLASSES[timeOnLcSeverity(ts)];
          return (
            <Tooltip content={ts ? new Date(ts).toLocaleString() : 'Not sent to LC'} placement="top">
              <span className={`cursor-help ${severityClass}`}>{display}</span>
            </Tooltip>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.sent_to_lc_at;
          const b = rowB.original.sent_to_lc_at;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
      },
      {
        id: 'equipment',
        header: 'Req',
        accessorFn: (row: SchedulingLeg) => {
          const labels: string[] = [];
          if (row.is_hazmat) labels.push('Hazmat');
          if (row.is_reefer) labels.push('Reefer');
          if (row.is_93k) labels.push('93K');
          if (row.is_tank) labels.push('Tank');
          if (row.is_hot) labels.push('Hot');
          if (row.is_overweight) labels.push('Overweight');
          if (row.is_daycab) labels.push('Daycab');
          if (row.is_triaxle) labels.push('Triaxle');
          if (row.is_swing) labels.push('Swing');
          if (row.is_ppe_required) labels.push('PPE');
          return labels;
        },
        cell: ({ row }) => renderEquipmentIcons(row.original),
        size: 120,
      },
      {
        id: 'flags',
        header: 'Flags',
        accessorFn: (row: SchedulingLeg) => {
          const labels: string[] = [];
          if (row.is_reschedule_required) labels.push('Reschedule');
          if (row.is_missing_weight) labels.push('Missing Weight');
          if (legBlockedForMissingHazDocs(row)) labels.push('Missing Haz Paperwork');
          if (row.is_cod) labels.push('COD');
          if (row.inbond_type?.trim()) labels.push('Bonded Cargo');
          const isRailOrder = ((row.mode === 'MLB' && row.move_type === 'IMPORT') || row.move_type === 'RAIL TO PORT') && (row.orig_location_type === 'RAIL' || row.orig_require_pickup === 'Y');
          if (isRailOrder && !row.rail_pickup_number?.trim()) labels.push('Missing Rail Pickup #');
          if (row.scheduling_comments?.trim()) labels.push('Has Comments');
          return labels;
        },
        cell: ({ row }) => renderFlagIcons(row.original),
        size: 100,
      },
      {
        accessorKey: 'trouble_ticket_count',
        header: 'TT',
        size: 50,
        cell: ({ row }) => (
          <TroubleTicketBadge
            tickets={row.original.trouble_tickets || []}
            count={row.original.trouble_ticket_count}
            onClick={() => openOrderModal(row.original.pronumber)}
          />
        ),
      },
      {
        id: 'pickup_origin',
        header: 'Pickup',
        size: 150,
        accessorFn: (row: SchedulingLeg) => row.pickup_name || row.orig_name || '',
        meta: { exportValue: (row: any) => row.pickup_name || row.orig_name || '' },
        cell: ({ row }) => {
          const name = row.original.pickup_name || row.original.orig_name;
          const hours = row.original.pickup_hours;
          const maxLane = row.original.pickup_max_lane_capacity;
          const maxDel = row.original.pickup_max_deliveries;
          const hasInfo = hours || maxLane != null || maxDel != null;
          return hasInfo ? (
            <Tooltip content={<div className="text-xs">{hours && <div>Hours: {hours}</div>}{maxLane != null && <div>Max Lane Capacity: {maxLane}</div>}{maxDel != null && <div>Max Deliveries: {maxDel}</div>}</div>} placement="top">
              <span>{name}</span>
            </Tooltip>
          ) : name;
        },
      },
      {
        id: 'delivery_dest',
        header: 'Delivery',
        size: 150,
        accessorFn: (row: SchedulingLeg) => row.delivery_name || row.dest_name || '',
        meta: { exportValue: (row: any) => row.delivery_name || row.dest_name || '' },
        cell: ({ row }) => {
          const name = row.original.delivery_name || row.original.dest_name;
          const hours = row.original.delivery_hours;
          const maxLane = row.original.delivery_max_lane_capacity;
          const maxDel = row.original.delivery_max_deliveries;
          const hasInfo = hours || maxLane != null || maxDel != null;
          return hasInfo ? (
            <Tooltip content={<div className="text-xs">{hours && <div>Hours: {hours}</div>}{maxLane != null && <div>Max Lane Capacity: {maxLane}</div>}{maxDel != null && <div>Max Deliveries: {maxDel}</div>}</div>} placement="top">
              <span>{name}</span>
            </Tooltip>
          ) : name;
        },
      },
      {
        accessorKey: 'miles',
        header: 'Miles',
        size: 70,
        cell: ({ getValue }) => {
          const miles = getValue() as number | null;
          return miles != null ? miles.toLocaleString() : '-';
        },
      },
      {
        accessorKey: 'last_free_day',
        header: 'LFD',
        size: 100,
        cell: ({ row }) => {
          const lfd = row.original.last_free_day;
          const prepull = row.original.proposed_prepull_date;
          const colorClass = getLfdColorClass(lfd, prepull);
          return <span className={colorClass}>{formatDate(lfd)}</span>;
        },
      },
      {
        accessorKey: 'cutoff_date',
        header: 'Cutoff',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          const colorClass = getCutoffPerDiemColorClass(value);
          return <span className={colorClass}>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'first_rcv_date',
        header: 'First RCV',
        size: 100,
        cell: ({ getValue }) => formatDate(getValue() as string | null),
      },
      {
        accessorKey: 'storage_cleared_date',
        header: 'Storage Cleared',
        size: 115,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return <span>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'target_delivery_date',
        header: 'Target Deliv',
        size: 160,
        meta: {
          bulkEdit: { type: 'date' } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const dateStr = row.original.target_delivery_date;
          const pronumber = row.original.pronumber;
          const fieldName = 'target_delivery_date';
          const dateValue = dateStr ? new Date(dateStr + 'T00:00:00') : null;
          const isLocked = row.original.schedule_status === 'FINALIZED';
          const editor = getFieldEditor(pronumber, fieldName);
          const onOrBy = row.original.target_on_or_by || '';

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <select
                value={onOrBy}
                onChange={(e) => {
                  updateLegSchedule(row.original.pronumber, {
                    target_on_or_by: e.target.value,
                  });
                }}
                disabled={isLocked}
                className="bg-white py-1 px-1 text-xs border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:focus:ring-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
                style={{ width: 52, minWidth: 52 }}
              >
                <option value="">-</option>
                <option value="ON">ON</option>
                <option value="BY">BY</option>
              </select>
              <EditableDatePicker
                value={dateValue}
                onChange={(newDate) => {
                  const formatted = newDate instanceof Date
                    ? formatDateToISO(newDate)
                    : null;
                  updateLegSchedule(row.original.pronumber, {
                    target_delivery_date: formatted,
                  });
                  if (formatted) {
                    broadcastFieldValue(pronumber, fieldName, formatted);
                    handleAutoSelect(row.original.pronumber);
                  }
                }}
                editor={editor}
                onFocus={() => !isLocked && broadcastFieldFocus(pronumber, fieldName)}
                onBlur={() => !isLocked && broadcastFieldBlur(pronumber, fieldName)}
                disabled={isLocked}
                rowIndex={row.index}
                field="target_delivery_date"
                dateStr={dateStr}
                onStartDrag={startDateDrag}
              />
            </div>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.target_delivery_date;
          const b = rowB.original.target_delivery_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
      },
      {
        accessorKey: 'customer_target_date',
        header: 'Cust Target',
        size: 105,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return <span>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'proposed_prepull_date',
        header: 'PrePull',
        size: 115,
        cell: ({ row }) => {
          // Prepull date is scheduler-only — LC sees it read-only.
          // To request a date, return the order to the scheduler with a comment.
          const dateStr = row.original.proposed_prepull_date;
          if (!dateStr) {
            // Prepull leg exists but no date set — finalize will be blocked
            if (row.original.has_prepull_leg) {
              return (
                <Tooltip content="Has a prepull leg but no prepull date — return to scheduler to set it">
                  <span className="inline-flex items-center gap-1">
                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 cursor-help" />
                    <span className="text-gray-400">-</span>
                  </span>
                </Tooltip>
              );
            }
            return <span className="text-gray-400">-</span>;
          }
          const isLocked = row.original.schedule_status === 'FINALIZED' || row.original.prepull_finalized || row.original.prepull_from_cms;
          return (
            <span className={isLocked ? 'inline-block px-1.5 py-0.5 rounded bg-ok text-ok-fg' : undefined}>
              {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}
            </span>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.proposed_prepull_date;
          const b = rowB.original.proposed_prepull_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
      },
      {
        accessorKey: 'proposed_appt_date',
        header: 'Appt Date',
        size: 115,
        cell: ({ row }) => {
          const dateStr = row.original.proposed_appt_date;
          const dateValue = dateStr ? new Date(dateStr + 'T00:00:00') : null;

          // LC cannot edit appt date - use Target Delivery Date + Return to Scheduler instead
          return (
            <span className="text-gray-500">{dateValue ? dateValue.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '-'}</span>
          );
        },
      },
      {
        id: 'appt_time_start',
        header: 'Appt 1',
        size: 85,
        meta: {
          bulkEdit: { type: 'text' } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const startTime = row.original.appt_time_start;
          const pronumber = row.original.pronumber;
          const fieldName = 'appt_time_start';

          return (
            <TimeInput
              value={startTime}
              disabled={row.original.prepull_needed}
              onChange={(formatted) => {
                updateLegSchedule(row.original.pronumber, {
                  appt_time_start: formatted,
                });
                if (formatted) {
                  broadcastFieldValue(pronumber, fieldName, formatted);
                }
              }}
              onFocus={() => broadcastFieldFocus(pronumber, fieldName)}
              onBlur={() => broadcastFieldBlur(pronumber, fieldName)}
            />
          );
        },
      },
      {
        id: 'appt_time_end',
        header: 'Appt 2',
        size: 85,
        meta: {
          bulkEdit: { type: 'text' } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const endTime = row.original.appt_time_end;
          const pronumber = row.original.pronumber;
          const fieldName = 'appt_time_end';

          return (
            <TimeInput
              value={endTime}
              disabled={row.original.prepull_needed}
              onChange={(formatted) => {
                updateLegSchedule(row.original.pronumber, {
                  appt_time_end: formatted,
                });
                if (formatted) {
                  broadcastFieldValue(pronumber, fieldName, formatted);
                }
              }}
              onFocus={() => broadcastFieldFocus(pronumber, fieldName)}
              onBlur={() => broadcastFieldBlur(pronumber, fieldName)}
            />
          );
        },
      },
      {
        accessorKey: 'schedule_status',
        header: 'Status',
        size: 140,
        meta: {
          exportValue: (row: SchedulingLeg) => row.prepull_needed ? 'Pre-pull needed' : SCHEDULE_STATUS_LABELS[row.schedule_status as ScheduleStatus] || row.schedule_status || '-',
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: 'PENDING_LC', label: 'Pending LC' },
              { value: 'PENDING_CUSTOMER', label: 'Pending Customer' },
              { value: 'HOLD', label: 'Hold' },
            ],
          } satisfies BulkEditMeta,
        },
        sortingFn: (rowA, rowB) => {
          // Pending LC always sorts to the top; remaining statuses sort by their display label
          const rank = (s: string | null | undefined) => (s === 'PENDING' ? 0 : 1);
          const a = rowA.original.schedule_status;
          const b = rowB.original.schedule_status;
          const rankDiff = rank(a) - rank(b);
          if (rankDiff !== 0) return rankDiff;
          const labelA = SCHEDULE_STATUS_LABELS[a as ScheduleStatus] || a || '';
          const labelB = SCHEDULE_STATUS_LABELS[b as ScheduleStatus] || b || '';
          return labelA.localeCompare(labelB);
        },
        cell: ({ row }) => {
          const leg = row.original;
          if (leg.prepull_needed) {
            return <span className="px-2 py-1 text-xs font-semibold rounded bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100">Pre-pull needed</span>;
          }
          const status = leg.schedule_status || '';
          // Editable statuses: PENDING (Pending LC), PENDING_CUSTOMER, HOLD
          const isEditable = ['PENDING', 'PENDING_CUSTOMER', 'HOLD'].includes(status);
          if (!isEditable) {
            return (
              <span className={`px-2 py-1 text-xs font-medium rounded ${SCHEDULE_STATUS_COLORS[status as ScheduleStatus] || 'bg-gray-100 text-gray-800'}`}>
                {SCHEDULE_STATUS_LABELS[status as ScheduleStatus] || status}
              </span>
            );
          }
          // Map schedule_status back to lc_next_step value for the dropdown
          const currentValue = leg.lc_next_step || (
            status === 'PENDING' ? 'PENDING_LC' :
            status === 'HOLD' ? 'HOLD' :
            status === 'PENDING_CUSTOMER' ? 'PENDING_CUSTOMER' : ''
          );
          return (
            <div className="relative group">
              <select
                className="w-full px-1 py-0.5 text-xs border rounded text-heading bg-panel border-gray-300 dark:border-gray-600"
                value={currentValue}
                onChange={(e) => {
                  const newValue = (e.target.value || null) as LCNextStep | null;
                  let newStatus: ScheduleStatus | null = null;
                  if (newValue === 'PENDING_LC') newStatus = 'PENDING';
                  else if (newValue === 'PENDING_CUSTOMER') newStatus = 'PENDING_CUSTOMER';
                  else if (newValue === 'HOLD') newStatus = 'HOLD';
                  updateLegSchedule(leg.pronumber, {
                    lc_next_step: newValue,
                    ...(newStatus && { schedule_status: newStatus }),
                    // HOLD parks the order without a date; return to scheduler to get a new one
                    ...(newStatus === 'HOLD' && { proposed_appt_date: null }),
                  });
                }}
              >
                <option value="PENDING_LC">Pending LC</option>
                <option value="PENDING_CUSTOMER">Pending Customer</option>
                <option value="HOLD">Hold</option>
              </select>
              <div
                className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-500 cursor-crosshair opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startDateDrag(row.index, 'lc_next_step', currentValue);
                }}
              />
            </div>
          );
        },
      },
    ],
    [selectedLegsSize, toggleLegSelection, selectLegs, deselectLegs, renderEquipmentIcons, renderFlagIcons, updateLegSchedule, handleAutoSelect, openOrderModal, openHistoryDrawer, startDateDrag]
  );

  const { columns, availableFields } = useAdditionalFieldColumns<SchedulingLeg>(
    fieldSelection, { baseColumns },
  );

  return (
    <>
    {showAchievementNotification && createPortal(
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      >
        <Notification
          type="success"
          title="Congrats!"
          message={<span>You found the <span className="font-semibold text-blue-900 dark:text-blue-300">You've Got Pros</span> Easter Egg</span>}
          showClose={true}
          onClose={() => setShowAchievementNotification(false)}
          autoDismiss={5000}
        />
      </div>,
      document.body
    )}
    <div className="flex flex-col h-full">
      {/* Filter Bar */}
      <FilterBar label="Filters:">
        <MultiSelect
          options={MOVE_TYPE_OPTIONS}
          value={[filters.move_type]}
          onChange={(values) => setFilters({ ...filters, move_type: (lastSelectedValue(values) || 'ALL') as 'ALL' | 'IMPORT' | 'EXPORT' | 'DOMESTIC' })}
          placeholder="Move Type"
          size="sm"
          className="min-w-[120px]"
        />
        <MultiSelect
          options={DELIVERY_TYPE_OPTIONS}
          value={filters.delivery_type ? [filters.delivery_type] : []}
          onChange={(values) => setFilters({ ...filters, delivery_type: lastSelectedValue(values) })}
          placeholder="All Delivery Types"
          showClearOption
          size="sm"
          className="min-w-[140px]"
        />
        <MultiSelect
          options={terminalOptions}
          value={filters.terminal || []}
          onChange={(values) => setFilters({ ...filters, terminal: values.length > 0 ? values : undefined })}
          placeholder="Regions"
          showClearOption
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={STATUS_OPTIONS}
          value={filters.status || []}
          onChange={(values) => {
            const prevValues = filters.status || [];
            const hadAll = prevValues.includes('ALL' as ScheduleStatus);
            const hasAll = values.includes('ALL');

            let newStatus: ScheduleStatus[];
            if (hasAll && !hadAll) {
              // User just selected "ALL" - clear others
              newStatus = ['ALL' as ScheduleStatus];
            } else if (hasAll && values.length > 1) {
              // User selected a specific status while "ALL" was selected - remove "ALL"
              newStatus = values.filter(v => v !== 'ALL') as ScheduleStatus[];
            } else if (values.length === 0) {
              // Nothing selected - default to "ALL"
              newStatus = ['ALL' as ScheduleStatus];
            } else {
              newStatus = values as ScheduleStatus[];
            }
            setFilters({ ...filters, status: newStatus });
          }}
          placeholder="All Statuses"
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={lcOptions}
          value={filters.logistic_coordinator || []}
          onChange={(values) => {
            const lcSelection = values.length > 0 ? values : undefined;
            // Prune selected customers that don't belong to the newly selected LC(s)
            let customer = filters.customer;
            if (lcSelection && customer?.length) {
              const customersByValue = new Map(
                (filterOptions?.customers || []).map((c) => [c.value, c])
              );
              customer = customer.filter((value) =>
                customersByValue.get(value)?.lcs?.some((lc) => lcSelection.includes(lc))
              );
              if (customer.length === 0) customer = undefined;
            }
            setFilters({ ...filters, logistic_coordinator: lcSelection, customer });
          }}
          placeholder="All LCs"
          enableSearch
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={customerOptions}
          value={filters.customer || []}
          onChange={(values) => setFilters({ ...filters, customer: values })}
          placeholder="All Customers"
          enableSearch
          size="sm"
          className="min-w-[140px]"
        />
        <button
          type="button"
          onClick={() => setFilters({
            move_type: 'ALL',
            terminal: undefined,
            delivery_type: undefined,
            status: ['PENDING', 'HOLD', 'PENDING_CUSTOMER'],
            customer: undefined,
            logistic_coordinator: undefined,
            pickup_from: undefined,
            deliver_to: undefined,
            restriction: undefined,
            appt_date: undefined,
            pronumber: undefined,
            include_null_status: false,
          })}
          className="text-sm text-muted hover:text-gray-700 dark:hover:text-gray-200"
        >
          Clear All Filters
        </button>
        <FilterBar.Spacer />
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search Pro #"
            value={filters.pronumber || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters({ ...filters, pronumber: value ? Number(value) : undefined });
            }}
            className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-[280px]"
          />
        </div>
        <button
          type="button"
          onClick={() => openHistoryDrawer(null)}
          className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200"
          title="Scheduling History"
          aria-label="Scheduling History"
        >
          <ClockIcon className="w-5 h-5" />
        </button>
      </FilterBar>
      {/* Advanced Filters Bar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-2 flex items-start justify-between">
        {/* Left side - Advanced filters */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted">Advanced:</span>
          <MultiSelect
            options={pickupLocationOptions}
            value={filters.pickup_from ? [filters.pickup_from] : []}
            onChange={(values) => setFilters({ ...filters, pickup_from: lastSelectedValue(values) })}
            placeholder="Pickup From"
            enableSearch
            showClearOption
            loading={loadingOptions}
            size="sm"
            className="min-w-[160px]"
          />
          <MultiSelect
            options={deliveryLocationOptions}
            value={filters.deliver_to ? [filters.deliver_to] : []}
            onChange={(values) => setFilters({ ...filters, deliver_to: lastSelectedValue(values) })}
            placeholder="Deliver To"
            enableSearch
            showClearOption
            loading={loadingOptions}
            size="sm"
            className="min-w-[160px]"
          />
          <MultiSelect
            options={RESTRICTION_OPTIONS}
            value={filters.restriction ? [filters.restriction] : []}
            onChange={(values) => setFilters({ ...filters, restriction: lastSelectedValue(values) })}
            placeholder="Restrictions"
            enableSearch
            showClearOption
            size="sm"
            className="min-w-[160px]"
          />
          <MultiSelect
            options={PREPULL_FILTER_OPTIONS}
            value={filters.prepull_filter ? [filters.prepull_filter] : []}
            onChange={(values) => setFilters({ ...filters, prepull_filter: lastSelectedValue(values) })}
            placeholder="Prepulls"
            showClearOption
            size="sm"
            className="min-w-[160px]"
          />
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={filters.appt_date ? new Date(filters.appt_date + 'T00:00:00') : null}
            onChange={(val) => {
              if (val instanceof Date) {
                const yyyy = val.getFullYear();
                const mm = String(val.getMonth() + 1).padStart(2, '0');
                const dd = String(val.getDate()).padStart(2, '0');
                setFilters({ ...filters, appt_date: `${yyyy}-${mm}-${dd}` });
              } else {
                setFilters({ ...filters, appt_date: undefined });
              }
            }}
            placeholder="Appt Date"
            autoApply
            inputVariant="compact"
            usePortal
            labelType="none"
          />
        </div>
        {/* Right side - Status Counts Widget */}
        <LCStatusCountsWidget legs={filteredGroupedLegs} />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4">
        {error && (
          <div className="mb-4 p-4 text-sm text-red-800 bg-red-100 rounded-lg dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}
        <TanTable
          data={groupedLegs}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          // Move Type ships as an available column but off by default — users
          // opt in via the show/hide-columns picker (choice persists).
          defaultColumnVisibility={{ move_type: false }}
          trackViewedColumnId="pronumber"
          loading={loading}
          cardTitle={tableTitle}
          settingsKey="logistics-coordinator"
          renderExpandedContent={renderExpandedContent}
          lockedColumns={['select']}
          getRowId={(row) => String(row.pronumber)}
          getRowClassName={(row) => getSchedulingRowClassName(row, newPronumbers)}
          enablePagination
          globalPageSize={50}
          hideAllRowsOption
          disableVirtualization
          enableColumnFilters
          onFilteredRowsChange={setFilteredGroupedLegs}
          onBulkEdit={handleColumnBulkEdit}
        />
      </div>

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isModalOpen && selectedPro !== null}
        onClose={closeOrderModal}
      />

      {/* Scheduling History Drawer */}
      <SchedulingHistoryDrawer
        isOpen={historyDrawer.open}
        onClose={() => setHistoryDrawer({ open: false, pronumber: null })}
        initialPronumber={historyDrawer.pronumber ?? undefined}
      />

      {/* Finalize Modal */}
      <FinalizeModal
        isOpen={isFinalizeModalOpen}
        onClose={() => {
          if (!finalizing) {
            setIsFinalizeModalOpen(false);
            setLegsToFinalize([]);
          }
        }}
        onConfirm={confirmFinalize}
        selectedCount={legsToFinalize.length}
        legs={legsToFinalize}
        isLoading={finalizing}
        allowPrepullOnly
      />

      {/* Bulk Edit Modal */}
      <LCBulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        onConfirm={confirmBulkEdit}
        selectedCount={selectedLegs.size}
      />

      {/* Return to Scheduler Modal */}
      <ReturnToSchedulerModal
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        onConfirm={confirmReturn}
        selectedCount={selectedLegs.size}
        existingTargetCount={selectedWithTargetDate}
        prepullOnlyCount={legs.filter(leg => selectedLegs.has(String(leg.pronumber)) && leg.prepull_needed).length}
      />
    </div>
    </>
  );
}

export default function LogisticsCoordinator() {
  return (
    <ViewedProsProvider>
      <LogisticsCoordinatorContent />
    </ViewedProsProvider>
  );
}
